import _ from "lodash";
import React from "react";
import classes from "./CheckboxGroup.scss";
import classNames from "classnames";
import CheckListContainer from "UIComponents/CheckListContainer";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import PropTypes from "prop-types";
import { colors } from "Constants";
import { compose } from "lib/apolloClient";
import { multiLineEllipsis } from "Utils";
import { Checkbox } from "@toddle-edu/ds-web";

const styles = {
  labelStyle: {
    flexBasis: "30%",
  },
  content: {
    color: colors.neutral400,
    fontSize: "1.4rem",
  },
};

class CheckboxGroup extends React.PureComponent {
  constructor(props) {
    super(props);
  }

  handleGroupCheckboxUpdate = (group, status, disabledList) => {
    const { value, options } = this.props;
    const selectedGroup = _.find(options, ({ label }) => label === group);
    let selectedGroupValues = _.map(
      selectedGroup.options,
      option => option.value
    );

    if (disabledList) {
      selectedGroupValues = _.filter(
        selectedGroupValues,
        selectedGroupValue =>
          _.findIndex(
            disabledList,
            disabledItem => disabledItem === selectedGroupValue
          ) === -1
      );
    }

    let updatedValues = [];

    if (status) updatedValues = _.uniq([...value, ...selectedGroupValues]);
    else
      updatedValues = _.filter(
        value,
        id => !_.includes(selectedGroupValues, id)
      );

    this.props.updateInputField(updatedValues);
  };

  handleCheckboxUpdate = _updatedValues => {
    const [updatedValues] = Object.values(_updatedValues);
    this.props.updateInputField(updatedValues);
  };

  toggleSelectAllOptions = (status, disabledList) => {
    const { options } = this.props;
    let updatedValues = [];

    if (status) {
      _.forEach(options, group => {
        const { options: groupOptions } = group;
        updatedValues = [...updatedValues, ..._.map(groupOptions, "value", [])];
      });
      updatedValues = _.uniq(updatedValues);
    } else updatedValues = [];
    if (disabledList) {
      updatedValues = _.filter(
        updatedValues,
        updatedValue =>
          _.findIndex(
            disabledList,
            disabledItem => disabledItem === updatedValue
          ) === -1
      );
    }

    this.props.updateInputField(updatedValues);
  };

  getSelectAllStatus = () => {
    const { options, value } = this.props;
    let allOptionsSelected = true;
    const groupStatus = {};

    _.forEach(options, group => {
      const { label, options: groupOptions } = group;
      const allSelectedInGroup = _.every(
        groupOptions,
        option =>
          _.includes(value, option.value) ||
          _.includes(this.props.disabledList, option.value)
      );
      groupStatus[label] = allSelectedInGroup;
      allOptionsSelected = allOptionsSelected && allSelectedInGroup;
    });
    return {
      allOptionsSelected,
      groupStatus,
    };
  };

  render() {
    const {
      options,
      value,
      searchText,
      t,
      checkboxGroupName,
      checkboxItemStyle,
      disabledList,
      tooltipPosition,
      checkboxItemLabelStyle,
      showChecklistItemTooltip,
      checklistItemTooltipPosition,
      checkBoxGroupContainerStyle,
      listItemStyle,
      showGroupToggleV2,
      groupContainerStyle,
      groupNameStyle,
      showSelectAllAsGroup,
      selectAllLabel,
    } = this.props;

    if (!options.length && searchText)
      return <div className={classes.noMatchFound}>No match found.</div>;

    const { allOptionsSelected, groupStatus } = this.getSelectAllStatus();
    return (
      <div
        className={classNames(
          classes.checkBoxGroupContainer,
          checkBoxGroupContainerStyle
        )}
      >
        <div className={classes.header}>
          {showSelectAllAsGroup ? (
            <div className={classes.groupName}>
              {t("all_with_label", { label: checkboxGroupName })}
            </div>
          ) : null}
          <div>
            <Checkbox
              isChecked={allOptionsSelected}
              onChange={() =>
                this.toggleSelectAllOptions(!allOptionsSelected, disabledList)
              }
              style={{
                alignItems: "center",
              }}
            >
              <span style={checkboxItemLabelStyle}>
                {_.size(selectAllLabel)
                  ? selectAllLabel
                  : t("select_all_with_label", { label: checkboxGroupName })}
              </span>
            </Checkbox>
          </div>
        </div>

        {_.map(options, (group, index) => {
          const { label: groupName, options: groupOptions } = group;

          const handleGroupToddle = () =>
            this.handleGroupCheckboxUpdate(
              groupName,
              !groupStatus[groupName],
              disabledList
            );

          return (
            <div
              key={`${index}-${groupName}`}
              className={classes.container}
              style={groupContainerStyle}
            >
              <div className={classes.header}>
                {showGroupToggleV2 ? (
                  <Checkbox
                    isChecked={groupStatus[groupName]}
                    onChange={handleGroupToddle}
                  >
                    <div className={classes.groupName} style={groupNameStyle}>
                      {groupName}
                    </div>
                  </Checkbox>
                ) : (
                  <div className={classes.groupName} style={groupNameStyle}>
                    {groupName}
                  </div>
                )}
                {!showGroupToggleV2 ? (
                  <div>
                    <Checkbox
                      isChecked={_.get(groupStatus, groupName)}
                      onChange={handleGroupToddle}
                    >
                      <span style={checkboxItemLabelStyle}>
                        {t("all_classes_with_label", { label: groupName })}
                      </span>
                    </Checkbox>
                  </div>
                ) : null}
              </div>

              <CheckListContainer
                name={groupName}
                options={groupOptions}
                value={value}
                updateInputField={this.handleCheckboxUpdate}
                checkboxItemStyle={checkboxItemStyle}
                checkboxItemLabelStyle={checkboxItemLabelStyle}
                listItemStyle={listItemStyle}
                fillColor={colors.blue400}
                CheckBoxLabelStyles={{
                  ...multiLineEllipsis({ lineHeight: 1.4, lineCount: 1 }),
                }}
                labelStyle={styles.labelStyle}
                showAllOption={false}
                disabledList={disabledList}
                tooltipPosition={tooltipPosition}
                showChecklistItemTooltip={showChecklistItemTooltip}
                checklistItemTooltipPosition={checklistItemTooltipPosition}
              />
            </div>
          );
        })}
      </div>
    );
  }
}

CheckboxGroup.defaultProps = {
  ...UIBaseComponent.defaultProps,
  showChecklistItemTooltip: false,
  checklistItemTooltipPosition: "bottom",
  listItemStyle: { flexBasis: "50%" },
  showGroupToggleV2: false,
  groupContainerStyle: {},
  groupNameStyle: {},
  showSelectAllAsGroup: true,
  selectAllLabel: "",
};

CheckboxGroup.propTypes = {
  ...UIBaseComponent.propTypes,
  showChecklistItemTooltip: PropTypes.bool,
  checklistItemTooltipPosition: PropTypes.string,
  listItemStyle: PropTypes.object,
  showGroupToggleV2: PropTypes.bool,
  groupContainerStyles: PropTypes.object,
  groupNameStyle: PropTypes.object,
  showSelectAllAsGroup: PropTypes.bool,
  selectAllLabel: PropTypes.string,
};

export default compose(I18nHOC({ resource: "common" }))(CheckboxGroup);
